import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Upload, FileCheck, ChevronLeft, AlertCircle } from 'lucide-react';

const BRANCHES = ['CSE', 'EEE', 'CIVIL', 'MECH', 'AERO', 'OTHER'];

export default function StudentRegistration() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    rollNumber: '',
    course: 'B.Tech',
    year: '1',
    branch: 'CSE',
    guardianName: '',
    guardianPhone: '',
    address: '',
    permanentAddress: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [files, setFiles] = useState({ aadhar: null, collegeId: null });
  const [fileErrors, setFileErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files: uploadedFiles } = e.target;
    const file = uploadedFiles[0];

    if (!file) return;

    // Validate file type
    if (name === 'aadhar' && file.type !== 'application/pdf') {
      setFileErrors({ ...fileErrors, aadhar: 'Aadhar must be in PDF format only' });
      return;
    }

    if (name === 'collegeId' && !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      setFileErrors({ ...fileErrors, collegeId: 'College ID must be PDF, JPG, or PNG' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFileErrors({ ...fileErrors, [name]: 'File size must be less than 5MB' });
      return;
    }

    setFiles({ ...files, [name]: file });
    setFileErrors({ ...fileErrors, [name]: '' });
  };

  const validateStep1 = () => {
    if (!form.name || !form.email || !form.password || !form.confirmPassword || !form.phone) {
      toast.error('Please fill all fields');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.rollNumber || !form.branch) {
      toast.error('Please fill all required fields');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!form.guardianName || !form.guardianPhone || !form.address || !form.permanentAddress) {
      toast.error('Please fill all required fields');
      return false;
    }
    if (!files.aadhar || !files.collegeId) {
      toast.error('Please upload both Aadhar and College ID');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        formData.append(key, form[key]);
      });
      formData.append('aadhar', files.aadhar);
      formData.append('collegeId', files.collegeId);

      console.log('📤 Submitting form with files:');
      console.log('   Aadhar:', files.aadhar?.name);
      console.log('   College ID:', files.collegeId?.name);

      const { data } = await authAPI.registerStudent(formData);
      toast.success(data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      console.error('❌ Registration error:', err);
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 flex items-center justify-center p-4 py-12">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)`,
        backgroundSize: '20px 20px'
      }} />

      <div className="w-full max-w-2xl relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Login
          </Link>
          <div className="text-sm text-slate-400">
            Step <span className="text-primary-400 font-semibold">{step}</span> of 3
          </div>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-2xl font-semibold text-white mb-6">Student Registration</h2>

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <form className="space-y-4">
              <p className="text-slate-400 text-sm mb-4">Create your account with login credentials</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleInputChange}
                    placeholder="e.g., 9876543210 or +91 9876543210"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleInputChange}
                    placeholder="Min. 6 characters"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirm Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Re-enter password"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleNext}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 rounded-lg transition mt-6"
              >
                Next Step
              </button>
            </form>
          )}

          {/* Step 2: Academic Info */}
          {step === 2 && (
            <form className="space-y-4">
              <p className="text-slate-400 text-sm mb-4">Fill in your academic details</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Roll Number *</label>
                  <input
                    type="text"
                    name="rollNumber"
                    value={form.rollNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., CS2021001"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Branch *</label>
                  <select
                    name="branch"
                    value={form.branch}
                    onChange={handleInputChange}
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {BRANCHES.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Course</label>
                  <input
                    type="text"
                    name="course"
                    value={form.course}
                    onChange={handleInputChange}
                    placeholder="e.g., B.Tech"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Year</label>
                  <select
                    name="year"
                    value={form.year}
                    onChange={handleInputChange}
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {[1, 2, 3, 4].map(y => (
                      <option key={y} value={y}>Year {y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 font-semibold py-2.5 rounded-lg transition"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 rounded-lg transition"
                >
                  Next Step
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Address & Documents */}
          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-slate-400 text-sm mb-4">Complete your profile and upload documents</p>

              {/* Address Section */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Address Information *</h3>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleInputChange}
                  placeholder="Current/Mailing Address"
                  className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                />
                <input
                  type="text"
                  name="permanentAddress"
                  value={form.permanentAddress}
                  onChange={handleInputChange}
                  placeholder="Permanent Address"
                  className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    name="state"
                    value={form.state}
                    onChange={handleInputChange}
                    placeholder="State"
                    className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <input
                  type="text"
                  name="pincode"
                  value={form.pincode}
                  onChange={handleInputChange}
                  placeholder="Pincode"
                  className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 mt-2"
                />
              </div>

              {/* Guardian Section */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Guardian Information *</h3>
                <input
                  type="text"
                  name="guardianName"
                  value={form.guardianName}
                  onChange={handleInputChange}
                  placeholder="Guardian Name"
                  className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                />
                <input
                  type="tel"
                  name="guardianPhone"
                  value={form.guardianPhone}
                  onChange={handleInputChange}
                  placeholder="Guardian Phone"
                  className="w-full bg-slate-700/30 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Documents Section */}
              <div className="bg-slate-700/20 border border-slate-600 rounded-xl p-4 space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  Required Documents *
                </h3>

                {/* Aadhar Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Aadhar Card (PDF only)</label>
                  <label className="flex flex-col items-center justify-center w-full p-4 bg-slate-700/30 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-primary-500 transition">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-6 h-6 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-400">
                        {files.aadhar ? (
                          <span className="text-emerald-400 flex items-center gap-1">
                            <FileCheck className="w-4 h-4" />
                            {files.aadhar.name}
                          </span>
                        ) : (
                          'Click to upload PDF'
                        )}
                      </p>
                    </div>
                    <input
                      type="file"
                      name="aadhar"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  {fileErrors.aadhar && <p className="text-red-400 text-xs mt-1">{fileErrors.aadhar}</p>}
                </div>

                {/* College ID Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">College ID (PDF, JPG, or PNG)</label>
                  <label className="flex flex-col items-center justify-center w-full p-4 bg-slate-700/30 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-primary-500 transition">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-6 h-6 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-400">
                        {files.collegeId ? (
                          <span className="text-emerald-400 flex items-center gap-1">
                            <FileCheck className="w-4 h-4" />
                            {files.collegeId.name}
                          </span>
                        ) : (
                          'Click to upload file'
                        )}
                      </p>
                    </div>
                    <input
                      type="file"
                      name="collegeId"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  {fileErrors.collegeId && <p className="text-red-400 text-xs mt-1">{fileErrors.collegeId}</p>}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 font-semibold py-2.5 rounded-lg transition"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-600 text-white font-semibold py-2.5 rounded-lg transition"
                >
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </button>
              </div>
            </form>
          )}

          {/* Info Box */}
          <div className="mt-6 p-4 bg-slate-700/30 border border-slate-600 rounded-lg">
            <p className="text-sm text-slate-400">
              ℹ️ Your registration will be reviewed by the hostel administrator. You will receive an email once it's approved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
